import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Button, IconButton, InputAdornment, LinearProgress, TextField, Tooltip} from "@mui/material";
import {
    Close, CloudUploadOutlined, DeleteOutlineOutlined, FileDownloadOutlined, Instagram, OpenInNew
} from "@mui/icons-material";
import styles from "./EditInfluencer.module.scss";
import Modal, {MODAL_SIZE_X_SMALL} from "../../../common/modal/Modal";
import {
    deleteInfluencerVideo,
    getInfluencerVideos,
    updateInfluencerVideo,
    uploadInfluencerVideo
} from "../../../services/paidProjectsService";

const isReelsUrl = (url) => !url || /^https:\/\/(www\.)?instagram\.com\//.test(url.trim());

const NotesField = (props) =>
    <TextField size="small" multiline minRows={5} maxRows={14} placeholder="Feedback, revisions, approvals…"
               className={styles.notes} {...props}/>;

const ReelsField = ({value, ...props}) =>
    <TextField size="small" placeholder="Instagram reels URL" value={value} error={!isReelsUrl(value)}
               InputProps={{startAdornment: <InputAdornment position="start"><Instagram fontSize="small"/></InputAdornment>}}
               {...props}/>;

const VideoCard = ({video, onChanged, notify}) => {
    const [comment, setComment] = useState(video.comment || '');
    const [reelsUrl, setReelsUrl] = useState(video.reels_url || '');
    const [confirmingDelete, setConfirmingDelete] = useState(false);
    const dirty = comment !== (video.comment || '') || reelsUrl !== (video.reels_url || '');

    const save = async () => {
        if (!isReelsUrl(reelsUrl)) return notify('Reels URL must be an instagram.com link');
        try {
            await updateInfluencerVideo({id: video.id, comment, reelsUrl: reelsUrl.trim()});
            onChanged({...video, comment, reels_url: reelsUrl.trim()});
            notify('Video saved');
        } catch {
            notify('Could not save video');
        }
    };

    // Soft delete: the row is flagged is_deleted and the file stays in GCS.
    const remove = async () => {
        try {
            await deleteInfluencerVideo(video.id);
            onChanged(null);
        } catch {
            notify('Could not delete video');
        }
    };

    return (
        <div className={`${styles.videoCard} ${dirty ? styles.dirty : ''}`}>
            <video src={video.play_url} controls preload="metadata" playsInline/>
            <div className={styles.videoBody}>
                <div className={styles.videoHead}>
                    <span>{new Date(video.created_at).toLocaleString([], {dateStyle: 'medium', timeStyle: 'short'})}</span>
                    {!!video.reels_url &&
                        <Tooltip title="Open reels"><IconButton size="small" href={video.reels_url} target="_blank"
                                                                rel="noreferrer"><OpenInNew fontSize="small"/></IconButton></Tooltip>}
                    <Tooltip title="Download"><IconButton size="small" href={video.download_url || video.play_url}
                                                          download><FileDownloadOutlined fontSize="small"/></IconButton></Tooltip>
                    <Tooltip title="Delete"><IconButton size="small" className={styles.danger} onClick={() => setConfirmingDelete(true)}>
                        <DeleteOutlineOutlined fontSize="small"/></IconButton></Tooltip>
                </div>
                <NotesField value={comment} onChange={e => setComment(e.target.value)}/>
                <div className={styles.videoFoot}>
                    <ReelsField value={reelsUrl} onChange={e => setReelsUrl(e.target.value)}/>
                    <Button size="small" variant="contained" disableElevation disabled={!dirty} onClick={save}>Save</Button>
                </div>
            </div>
            <Modal isOpen={confirmingDelete} title="Delete video"
                   content="This video will be removed from the influencer. Are you sure?"
                   confirmationBtnText="Delete" buttonColor="error" approveBtnStartIcon={<DeleteOutlineOutlined/>}
                   onConfirmation={remove} onCancelation={() => setConfirmingDelete(false)}
                   onClose={() => setConfirmingDelete(false)} modalSize={MODAL_SIZE_X_SMALL}/>
        </div>
    );
};

const InfluencerVideos = ({projectId, affId, notify}) => {
    const [videos, setVideos] = useState([]);
    const [file, setFile] = useState(null);
    const [comment, setComment] = useState('');
    const [reelsUrl, setReelsUrl] = useState('');
    // undefined = idle, number = uploading %, null = server compressing
    const [progress, setProgress] = useState(undefined);
    const [dragging, setDragging] = useState(false);
    const fileInput = useRef(null);
    const busy = progress !== undefined;
    const preview = useMemo(() => file && URL.createObjectURL(file), [file]);
    useEffect(() => () => preview && URL.revokeObjectURL(preview), [preview]);

    useEffect(() => {
        getInfluencerVideos(projectId, affId)
            .then(r => setVideos(r.data?.videos || []))
            .catch(() => notify('Could not load videos'));
    }, [projectId, affId]); // eslint-disable-line react-hooks/exhaustive-deps

    const upload = async () => {
        if (!isReelsUrl(reelsUrl)) return notify('Reels URL must be an instagram.com link');
        setProgress(0);
        try {
            const r = await uploadInfluencerVideo({
                projectId, affId, file, comment, reelsUrl: reelsUrl.trim(), onProgress: setProgress
            });
            setVideos(v => [r.data.video, ...v]);
            clearFile();
            setComment('');
            setReelsUrl('');
            notify('Video uploaded');
        } catch {
            notify('Video upload failed');
        } finally {
            setProgress(undefined);
        }
    };

    const replace = (id) => (next) =>
        setVideos(v => next ? v.map(x => x.id === id ? next : x) : v.filter(x => x.id !== id));

    const pick = (f) => f && f.type.startsWith('video/') ? setFile(f) : notify('Please choose a video file');
    const clearFile = () => {
        setFile(null);
        fileInput.current.value = '';
    };

    return (
        <section className={styles.pane}>
            <div className={styles.label}>Videos{!!videos.length && ` · ${videos.length}`}</div>
            <input ref={fileInput} type="file" accept="video/*" hidden onChange={e => pick(e.target.files[0])}/>
            <div className={styles.videoGrid}>
                {!file
                    ? <button type="button" className={`${styles.dropzone} ${dragging ? styles.dragging : ''}`}
                              onClick={() => fileInput.current.click()}
                              onDragOver={e => { e.preventDefault(); setDragging(true); }}
                              onDragLeave={() => setDragging(false)}
                              onDrop={e => { e.preventDefault(); setDragging(false); pick(e.dataTransfer.files[0]); }}>
                        <CloudUploadOutlined/>
                        <strong>Add video</strong>
                        <span>Drop a file or click to browse · compressed to 1080p on upload</span>
                    </button>
                    : <div className={`${styles.videoCard} ${styles.composer}`}>
                        <video src={preview} muted playsInline controls/>
                        <div className={styles.videoBody}>
                            <div className={styles.videoHead}>
                                <strong title={file.name}>{file.name}</strong>
                                <span>{(file.size / 1048576).toFixed(1)} MB</span>
                                <IconButton size="small" disabled={busy} onClick={clearFile}><Close fontSize="small"/></IconButton>
                            </div>
                            <NotesField value={comment} disabled={busy} onChange={e => setComment(e.target.value)}/>
                            {busy
                                ? <div className={styles.progress}>
                                    <LinearProgress variant={progress === null ? 'indeterminate' : 'determinate'}
                                                    value={progress || 0}/>
                                    <span>{progress === null ? 'Optimizing video…' : `Uploading ${progress}%`}</span>
                                </div>
                                : <div className={styles.videoFoot}>
                                    <ReelsField value={reelsUrl} onChange={e => setReelsUrl(e.target.value)}/>
                                    <Button size="small" variant="contained" disableElevation onClick={upload}>Upload</Button>
                                </div>}
                        </div>
                    </div>}
                {videos.map(v => <VideoCard key={v.id} video={v} onChanged={replace(v.id)} notify={notify}/>)}
            </div>
        </section>
    );
};

export default InfluencerVideos;
